import { useCallback, useEffect, useRef } from 'react'

import { actAsBuyer, clearActAsBuyer } from '@app/api'
import { runAppInit } from '@app/appInit'
import config from '@app/config'
import {
  useAuth,
  useAppDispatch,
  useMainBuyerOrRenter,
  useUser,
} from '@app/hooks'
import { deleteCookie, getCookie } from '@app/services'
import { store } from '@app/store'
import { getTokenAccountId, getTokenUserId } from '@app/store/auth'
import { getCurrentUserThunk } from '@app/store/user'
import { isBuyerTokenMode, isBuyerView } from '@app/utils'
import { removeBuyerViewQueryParam } from '@app/utils/url/removeBuyerViewQueryParam'

import { BUYER_MODE_EXIT_REASON, BUYER_VIEW_ACCOUNT_ID } from './constants'
import type { UseBuyerModeLifecycleProps } from './types'

export const useBuyerModeLifecycle = ({
  conversation,
  conversationId,
  isStaff,
  isSpamConversation,
  fetching,
  fetchInitialStateErrorStatus,
}: UseBuyerModeLifecycleProps) => {
  const dispatch = useAppDispatch()
  const authState = useAuth()
  const userState = useUser()

  const mainBuyerOrRenter = useMainBuyerOrRenter(conversation)
  const currentTokenUserId = getTokenUserId(authState)
  const currentTokenAccountId = getTokenAccountId(authState)
  const isBuyer = isBuyerView()

  const buyerSwitchInProgressRef = useRef(false)
  const buyerRestoreInProgressRef = useRef(false)
  const staffBootstrapRequestedRef = useRef(false)

  useEffect(() => {
    const hasBuyerToken = isBuyerTokenMode()
    if (isBuyer && userState.fetchingSucceed && !isStaff && !hasBuyerToken) {
      removeBuyerViewQueryParam()
    }
  }, [isBuyer, isStaff, userState.fetchingSucceed])

  const exitBuyerMode = useCallback((reason: string) => {
    if (buyerRestoreInProgressRef.current) {
      return
    }

    buyerRestoreInProgressRef.current = true
    removeBuyerViewQueryParam()

    const hasBuyerToken = Boolean(
      getCookie(config.variables.buyer_access_token)
    )
    const clearBuyerModePromise = hasBuyerToken
      ? clearActAsBuyer().finally(() => {
        deleteCookie(config.variables.buyer_access_token)
      })
      : Promise.resolve()

    void clearBuyerModePromise
      .catch((error) => {
        console.error(`Failed to clear buyer mode (${reason})`, error)
      })
      .then(() => {
        const state = store.getState()
        const restoredTokenUserId = getTokenUserId(state.auth)
        const restoredTokenAccountId = getTokenAccountId(state.auth)
        const hasRestoredStaffSession =
          state.user.fetchingSucceed &&
          state.user.currentUser.id === restoredTokenUserId &&
          state.user.account.id === restoredTokenAccountId

        if (hasRestoredStaffSession) {
          return
        }

        return runAppInit()
      })
      .catch((error) => {
        console.error(
          `Failed to reinitialize app after exiting buyer mode (${reason})`,
          error
        )
      })
      .finally(() => {
        buyerRestoreInProgressRef.current = false
      })
  }, [])

  useEffect(() => {
    if (isBuyer && conversationId === null) {
      const hasBuyerToken = Boolean(
        getCookie(config.variables.buyer_access_token)
      )

      if (hasBuyerToken) {
        exitBuyerMode(BUYER_MODE_EXIT_REASON.NO_CONVERSATION)
        return
      }

      removeBuyerViewQueryParam()
    }
  }, [isBuyer, conversationId, exitBuyerMode])

  useEffect(() => {
    const hasBuyerToken = Boolean(
      getCookie(config.variables.buyer_access_token)
    )
    const hasStaffToken = Boolean(getCookie(config.variables.b2b_access_token))
    const shouldBootstrapStaffFromB2B =
      isBuyer && !hasBuyerToken && hasStaffToken
    const hasCurrentUserForToken =
      userState.fetchingSucceed &&
      userState.currentUser.id === currentTokenUserId &&
      userState.account.id === currentTokenAccountId

    if (!shouldBootstrapStaffFromB2B) {
      staffBootstrapRequestedRef.current = false
    }

    if (
      shouldBootstrapStaffFromB2B &&
      !hasCurrentUserForToken &&
      !userState.fetchingPending &&
      !staffBootstrapRequestedRef.current &&
      currentTokenUserId !== null &&
      currentTokenAccountId !== null
    ) {
      staffBootstrapRequestedRef.current = true
      void dispatch(
        getCurrentUserThunk({
          userId: currentTokenUserId,
          accountId: currentTokenAccountId,
        })
      )
      return
    }

    if (shouldBootstrapStaffFromB2B && !hasCurrentUserForToken) {
      return
    }

    if (!isStaff) {
      return
    }

    if (isBuyer && isSpamConversation) {
      exitBuyerMode(BUYER_MODE_EXIT_REASON.SPAM_CONVERSATION)
      return
    }

    if (!isBuyer) {
      if (hasBuyerToken) {
        exitBuyerMode(BUYER_MODE_EXIT_REASON.NON_BUYER_VIEW)
      }
      return
    }

    const buyerUserId = mainBuyerOrRenter?.userId
    if (
      !buyerUserId ||
      buyerUserId === currentTokenUserId ||
      currentTokenAccountId === null
    ) {
      return
    }

    if (buyerSwitchInProgressRef.current) {
      return
    }

    buyerSwitchInProgressRef.current = true
    void actAsBuyer({
      userId: buyerUserId,
      accountId: BUYER_VIEW_ACCOUNT_ID,
    })
      .then(() => {
        return runAppInit()
      })
      .catch((error) => {
        console.error('Failed to switch to buyer view', error)
      })
      .finally(() => {
        buyerSwitchInProgressRef.current = false
      })
  }, [
    dispatch,
    isStaff,
    isBuyer,
    mainBuyerOrRenter?.userId,
    currentTokenUserId,
    currentTokenAccountId,
    userState.fetchingPending,
    userState.fetchingSucceed,
    userState.currentUser.id,
    userState.account.id,
    isSpamConversation,
    exitBuyerMode,
  ])

  useEffect(() => {
    if (
      isBuyer &&
      fetching === 'ERROR' &&
      fetchInitialStateErrorStatus === 403
    ) {
      exitBuyerMode(BUYER_MODE_EXIT_REASON.CONVERSATION_403)
    }
  }, [isBuyer, fetching, fetchInitialStateErrorStatus, exitBuyerMode])

  return {
    isBuyer,
    shouldDisplayLoading: isBuyer && isStaff,
  }
}
